/* eslint-disable no-unused-vars */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import Layout from '../components/Templates/Layout';
import MortgageRate from '../components/Pages/MortgageRate/MortgageRate';
import TrustPilotWidget from '../components/Atoms/TrustPilotWidget';
import CalculatorCTA from '../components/Molecules/CalculatorCTA';
import LenderCTA from '../components/Molecules/LenderCTA';
import RateTable from '../components/Atoms/RateTable';
import Accordion from '../components/Atoms/Accordion';
import MortgageRatesTableError from '../components/Molecules/MortgageRatesTableError';

import { fnParseHTMLWithPageData } from '../utilities/parseHTML';
import sanitizeHeader from '../utilities/sanitizeHeader';
import addMetaRobots from '../utilities/addMetaRobots';
import { globalTransformer } from '../graphql/transformers';
import { useMortgageRateApi } from '../utilities/mortgageRatesAPI/useMortgageRateApi';
import parseMortgagerates from '../utilities/parseMortgageRates';

import { testAttributes } from '../components/Molecules/CalculatorCTA/Constants';
import { testAttributes as testAttributesLender } from '../components/Molecules/LenderCTA/Constants';
import { NewMortgageRate } from '../components/Pages/NewMortgageRate';

const { isFeatureFlagEnabled } = require('../utilities/featureMap');

function MortgageRatesContainer({ data: queried, location = null }) 
{
    const { calcCtas, mortgageRates } = queried.features.nodes[0];
    const productCodes = mortgageRates?.productCodes || [];
    const { data, isLoading, error } = useMortgageRateApi({
        productCodes: [...productCodes],
    });

    const mortgageParsed = parseMortgagerates(data, 'mortgagepage');
    const { headerProps, footerProps } = globalTransformer({
        queried,
        location,
    });

    const { parse, parseHTMLWithPageData } = fnParseHTMLWithPageData({
        location,
    });

    const isNewMortgageRatesEnabled = isFeatureFlagEnabled(queried.optimizelyJSON, 'ql-newmortgage-config-flag');

    const pageContent = useMemo(() => 
    {
        if (isNewMortgageRatesEnabled) 
        {
            return (
                <NewMortgageRate 
                    breadcrumbs={calcCtas.breadcrumbs} 
                />
            );
        }

        const ratesTable = !isLoading && error ? [
            <MortgageRatesTableError
                  applyNowUrl={mortgageRates?.applyNowUrl}
            />,
        ]: 
        [
            <RateTable
                {...mortgageParsed}
                isLoading={isLoading}
                skeletonRows={
                    mortgageRates?.productCodes?.length || 3
                }
            />  
            ,
            <Accordion label="*Legal Disclosures">
                {
                    parseHTMLWithPageData(
                        queried.complianceMarkup.rawMarkdownBody
                    )?.parsed
                }
                {parseHTMLWithPageData(mortgageParsed.assumptions).parsed}
            </Accordion>,
        ];

        return (
            <MortgageRate 
                content={parse([
                    queried.heroMarkup.html,
                    ...ratesTable,
                    <CalculatorCTA
                        content={calcCtas.paymentCta}
                        testIds={testAttributes}
                    />,
                    queried.editorialMarkup.html,
                    <LenderCTA
                        content={calcCtas.lenderData}
                        testIds={testAttributesLender}
                    />,
                    queried.trustpilotMarkup.html,
                    <TrustPilotWidget />,
                ])} 
            />
        );
    }, [isNewMortgageRatesEnabled, isLoading, error, data, queried]);   

    return (
        <Layout
            headerProps={headerProps}
            footerProps={footerProps}
            breadcrumbProps={calcCtas.breadcrumbs}
            hasHero={isNewMortgageRatesEnabled}
        >
            {pageContent}
        </Layout>
    );
}

export default MortgageRatesContainer;

export const Head = ({ data: queried }) => 
{
    // @TODO Replace this line once we connect to WP. const { headMarkup, robots } = queried?.author?.cdmfields?.metadata;
    const { html: headMarkup, robots } = queried?.headMeta;
    const headMetaData = addMetaRobots(robots, headMarkup);
    return sanitizeHeader(headMetaData).parsed;
};

export const pageQuery = graphql`
    query MortgageratepageQuery {
        ...globals
        headMeta: markdownRemark(
            frontmatter: {
                _parent: { eq: "mortgagerates" }
                _ref: { eq: "meta" }
            }
        ) {
            html
        }
        heroMarkup: markdownRemark(
            frontmatter: {
                _parent: { eq: "mortgagerates" }
                _ref: { eq: "hero" }
            }
        ) {
            html
        }
        editorialMarkup: markdownRemark(
            frontmatter: {
                _parent: { eq: "mortgagerates" }
                _ref: { eq: "editorial" }
            }
        ) {
            html
        }
        trustpilotMarkup: markdownRemark(
            frontmatter: {
                _parent: { eq: "mortgagerates" }
                _ref: { eq: "trustpilot" }
            }
        ) {
            html
        }
        optimizelyJSON: featureFlags {
            name
            keyValueMap
        }
        features: allMortgagerateJson(
            filter: {
                _parent: { eq: "mortgagerates" }
                _ref: { eq: "features" }
            }
        ) {
            nodes {
                calcCtas {
                    title
                    breadcrumbs {
                        id
                        text
                        url
                    }
                    lenderData {
                        databaseId
                        label
                        sublabel
                        submenu {
                            nodes {
                                databaseId
                                label
                                src
                                testId
                            }
                        }
                    }
                    paymentCta {
                        databaseId
                        label
                        sublabel
                        submenu {
                            nodes {
                                databaseId
                                label
                                src
                                testId
                            }
                        }
                    }
                }
                mortgageRates {
                    productCodes
                    applyNowUrl
                }
            }
        }
    }
`;

MortgageRatesContainer.propTypes = {
    data: PropTypes.object.isRequired,
    location: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string,
        PropTypes.oneOf([null]),
    ]),
};
